import { useRouter } from 'next/router'
import { KeyboardEvent, VFC, useEffect, useRef, useState } from 'react'
import {
  createTag,
  createTagAndTagMapping,
} from 'src/api/companyMessageRoomTag'
import {
  createTagMapping,
  deleteTagMapping,
} from 'src/api/companyMessageRoomTagMapping'
import { CompanyMessageRoomTag } from 'src/api/types/companyMessageRoomTag'
import { GrayDotsIcon, SmallCloseIcon, TrashBoxIcon } from 'src/assets/svgs'
import { HEADER_HEIGHT } from 'src/components/Header/WideHeader/HeaderWrapper'
import { useMessageContext } from 'src/compositions/Messages/MessageContext'
import { RoomType, TagMapping } from 'src/models/Room'
import {
  _message_submenu,
  _message_tag_label,
  _messagesecondary,
  _tags_management_background,
  attentionColor,
  truewhite,
} from 'src/styles/colors'
import { fontWeightBold, fontWeightRegular } from 'src/styles/font'
import {
  convertMessagePageQuqry,
  convertTagIds,
  stringifyQueryToComma,
  toMessagePageQuery,
} from 'src/utils/routes'
import styled, { css } from 'styled-components'

type TagsManagementCommonProps = {
  positions: {
    top?: number
    left?: number
    right?: number
    bottom?: number
  }
  close: () => void
}
type TagsManagementProps = TagsManagementCommonProps & {
  tagMappings: TagMapping[]
  setTagMappings: (tagMappings: TagMapping[]) => void
  mutateRoom?: () => void
  mutateRooms?: () => void
  roomId: number
  roomType: RoomType
}
export const TagsManagement: VFC<TagsManagementProps> = ({
  tagMappings,
  setTagMappings,
  mutateRoom,
  mutateRooms,
  roomId,
  roomType,
  positions,
  close,
}) => {
  const { tags, mutateTags } = useMessageContext()
  // NOTE: ルーム一覧からタグ管理を開いた時はヘッダーのタグ管理を mutate します
  // ヘッダーからタグ管理を開いた時はルーム一覧のタグ管理を mutate します
  const mutateRoomFunc = () => {
    mutateRoom && mutateRoom()
    mutateRooms && mutateRooms()
  }
  // NOTE: タグ作成 & 作成したタグをルームに紐付けて、ルームに紐付けたタグを mutate して更新します
  const createTagAndTagMappingFunc = async (tagName: string) => {
    pushToManagementTags(tagName)
    const res = await createTagAndTagMapping(tagName, roomId, roomType)
    if (res.data) {
      // FIXME: mutate で更新しても画面描画が瞬時にされないため手動で配列を書き換える
      const filtered = tagMappings.filter(
        (tagMapping) => tagMapping.tagName !== tagName,
      )
      mutateTagMappings(filtered, {
        tagId: res.data.id,
        id: res.data.companyMessageRoomTagMappingId,
        tagName,
      })
      // NOTE: タグを新規作成したためタグ一覧を mutate します
      mutateTags && mutateTags()
      mutateRoomFunc()
    }
  }

  const createTagMappingFunc = async (tagId: number) => {
    const tag = tags.find((tag) => tag.id === tagId)
    if (!tag) return
    pushToManagementTags(tag.name || '')
    const res = await createTagMapping({
      company_message_room_tag_id: tagId,
      room_id: roomId,
      room_type: roomType,
    })
    if (res.data) {
      // FIXME: mutate で更新しても画面描画が瞬時にされないため手動で配列を書き換える
      const filtered = tagMappings.filter(
        (tagMapping) => tagMapping.tagId !== tagId,
      )
      mutateTagMappings(filtered, {
        tagId: tagId,
        id: res.data.id,
        tagName: tag.name,
      })
      mutateRoomFunc()
    }
  }

  const deleteTagMappingFunc = async (_tagId: number, tagMappingId: number) => {
    const filtered = tagMappings.filter(
      (tagMapping) => tagMapping.id !== tagMappingId,
    )
    setTagMappings(filtered)
    await deleteTagMapping(tagMappingId)
    mutateRoomFunc()
  }

  // NOTE: APIリクエスト前に配列にpushして瞬時にDOMを更新している
  const pushToManagementTags = (tagName: string) => {
    // NOTE: 連打対策
    const tag = tagMappings.find((tag) => tag.tagName === tagName)
    if (tag) return
    mutateTagMappings(tagMappings, {
      tagName,
      tagId: Math.random(),
      id: Math.random(),
    })
  }
  // NOTE: 配列を更新します
  const mutateTagMappings = (
    tagMappings: TagMapping[],
    newTagMapping: Pick<TagMapping, 'id' | 'tagId' | 'tagName'>,
  ) => setTagMappings([...tagMappings, { ...newTagMapping, roomId, roomType }])

  const managementTags = tagMappings.map((tagMapping) => ({
    tagMappingId: tagMapping.id,
    tagName: tagMapping.tagName,
    tagId: tagMapping.tagId,
  }))

  return (
    <TagManagemntComponent
      managementTags={managementTags}
      createTagAndTagMappingFunc={createTagAndTagMappingFunc}
      createTagMappingFunc={createTagMappingFunc}
      deleteTagMappingFunc={deleteTagMappingFunc}
      isSearch={false}
      positions={positions}
      close={close}
    />
  )
}

type SearchTagsManagementProps = TagsManagementCommonProps & {
  searchTags: CompanyMessageRoomTag[]
}
export const SearchTagsManagement: VFC<SearchTagsManagementProps> = ({
  searchTags,
  positions,
  close,
}) => {
  const router = useRouter()
  const query = convertMessagePageQuqry(router.query)
  const tagIds = convertTagIds(query.tag_ids)
  const { mutateTags } = useMessageContext()

  const createTagAndTagMappingFunc = async (tagName: string) => {
    const res = await createTag(tagName)
    const id = res?.data?.id
    if (!id) return
    router.push(
      toMessagePageQuery({
        pathname: router.pathname,
        query: {
          ...query,
          tag_ids: stringifyQueryToComma({
            tag_ids: [...tagIds, id],
          }),
        },
      }),
    )
    mutateTags && mutateTags()
  }

  const createTagMappingFunc = async (tagId: number) => {
    router.push(
      toMessagePageQuery({
        pathname: router.pathname,
        query: {
          ...query,
          tag_ids: stringifyQueryToComma({
            tag_ids: [...tagIds, tagId],
          }),
        },
      }),
    )
  }

  const deleteTagMappingFunc = async (tagId: number, _tagMappingId: number) => {
    router.push(
      toMessagePageQuery({
        pathname: router.pathname,
        query: {
          ...query,
          tag_ids: stringifyQueryToComma({
            tag_ids: tagIds.filter((id) => id !== tagId),
          }),
        },
      }),
    )
  }

  const managementTags: ManagementTag[] = searchTags.map((tag) => ({
    tagName: tag.name,
    tagId: tag.id,
  }))

  return (
    <TagManagemntComponent
      createTagAndTagMappingFunc={createTagAndTagMappingFunc}
      createTagMappingFunc={createTagMappingFunc}
      deleteTagMappingFunc={deleteTagMappingFunc}
      managementTags={managementTags}
      isSearch={true}
      positions={positions}
      close={close}
    />
  )
}

type ManagementTag = {
  tagMappingId?: number
  tagName: string
  tagId: number
}
type TagManagemntComponentType = {
  createTagAndTagMappingFunc: (tagName: string) => Promise<void>
  createTagMappingFunc: (tagId: number) => Promise<void>
  deleteTagMappingFunc: (_tagId: number, _tagMappingId: number) => Promise<void>
  managementTags: ManagementTag[]
  isSearch: boolean
}
const TagManagemntComponent: VFC<
  TagsManagementCommonProps & TagManagemntComponentType
> = ({
  createTagMappingFunc,
  createTagAndTagMappingFunc,
  deleteTagMappingFunc,
  managementTags,
  isSearch,
  positions,
  close,
}) => {
  const [tagName, setTagName] = useState<string>('')
  const [filteredTags, setFilteredTags] = useState<CompanyMessageRoomTag[]>([])
  const FIRST_INDEX = 0 // NOTE: タグの先頭のnumberです
  const DEFAULT_INDEX = -1 // NOTE: タグの初期値です
  const CREATE_TAG_INDEX = -2 // NOTE: 新規作成するタグのindexです
  const [currentIndex, setCurrentIndex] = useState<number>(DEFAULT_INDEX)
  const {
    tags,
    editTagId,
    setEditTagId,
    setIsDeleteTagModalOpen,
    editTagName,
    setEditTagName,
    setIsRenameTagModalOpen,
    setTagValidationMessage,
    setIsTagValidationModalOpen,
  } = useMessageContext()

  // NOTE: タグが入力済み or 作成済みのタグと同じ名前がなければ作成可能とする
  const canCreateTag =
    tagName !== '' && !tags.find((tag) => tag.name === tagName)
  const TAG_MAPPINGS_MAX_COUNT = 5 // ルームに紐付けるタグの上限
  const [isLengthError, setIsLengthError] = useState<boolean>(false)
  const displayTagMappingsCountError =
    isLengthError &&
    !isSearch &&
    managementTags.length >= TAG_MAPPINGS_MAX_COUNT
  const inputRef = useRef<HTMLInputElement>(null)
  const editInputRef = useRef<HTMLInputElement>(null)

  // createTagAndMutate タグを新規生成してmutateします
  const createTagAndMutate = async () => {
    if (!isSearch && managementTags.length >= TAG_MAPPINGS_MAX_COUNT) {
      setIsLengthError(true)
      return
    }
    await createTagAndTagMappingFunc(tagName)
    setTagName('')
    inputRef.current?.focus()
  }

  // NOTE: タグ名を入力したら既存のタグから曖昧検索で一致したものを表示
  useEffect(() => {
    if (tagName) {
      const filtered = tags.filter((tag) => tag.name.includes(tagName))
      setFilteredTags(filtered)
      // NOTE: 重複するタグがなければ 新規作成タグ を選択します
      if (canCreateTag && filtered.length === 0)
        setCurrentIndex(CREATE_TAG_INDEX)
      else setCurrentIndex(FIRST_INDEX)
    } else {
      setFilteredTags(tags)
      setCurrentIndex(DEFAULT_INDEX)
    }
  }, [tags, tagName, canCreateTag, DEFAULT_INDEX, CREATE_TAG_INDEX])

  // NOTE: 編集モーダルを開いたら編集inputをfocus, それ以外は新規inputをfocus
  useEffect(() => {
    if (editTagId) editInputRef.current?.focus()
    else inputRef.current?.focus()
  }, [editTagId])

  // onKeyPressInputTag Enterを押した時にタグを作成or紐付けするか管理します
  const onKeyPressInputTag = async (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return
    if (canCreateTag) {
      if (currentIndex === CREATE_TAG_INDEX) await createTagAndMutate()
    } else if (!canCreateTag) {
      const tagId = filteredTags[currentIndex]?.id
      await onClickCreateTagMapping(tagId)
    }
  }

  // onKeyDownCurrentIndex 上下の矢印キーを押した時に currentIndex を変更します
  const onKeyDownCurrentIndex = (event: KeyboardEvent<HTMLInputElement>) => {
    if (
      filteredTags.length === 0 ||
      !(
        event.key === 'ArrowDown' ||
        event.key === 'ArrowUp' ||
        event.key === 'Backspace'
      )
    )
      return

    const lastNumber = filteredTags.length - 1
    const isLast = currentIndex === lastNumber
    const isFirst = currentIndex === FIRST_INDEX
    // NOTE: 十字キーの下矢印を押した場合
    if (event.key === 'ArrowDown')
      if (
        currentIndex === DEFAULT_INDEX ||
        currentIndex === CREATE_TAG_INDEX ||
        (!canCreateTag && isLast)
      )
        setCurrentIndex(FIRST_INDEX)
      else if (canCreateTag && isLast) setCurrentIndex(CREATE_TAG_INDEX)
      else setCurrentIndex(currentIndex + 1)
    // NOTE: 十字キーの上矢印を押した場合
    if (event.key === 'ArrowUp')
      if (
        currentIndex === DEFAULT_INDEX ||
        currentIndex === CREATE_TAG_INDEX ||
        (!canCreateTag && isFirst)
      )
        setCurrentIndex(lastNumber)
      else if (canCreateTag && isFirst) setCurrentIndex(CREATE_TAG_INDEX)
      else setCurrentIndex(currentIndex - 1)
    if (event.key === 'Backspace')
      if (tagName === '') {
        // NOTE: inputが空の時にBackspaceを押した場合は末尾のタグを削除します
        const latestTag = managementTags[managementTags.length - 1]
        if (!latestTag) return
        deleteTagMappingFunc(latestTag.tagId, latestTag.tagMappingId || 0)
      }
  }

  // onClickCreateTagMapping ルームにタグを紐付けます
  const onClickCreateTagMapping = async (tagId: number) => {
    // NOTE: 検索タグ管理ではない & タグ紐付け数が上限に達している場合はエラーを表示します
    if (!isSearch && managementTags.length >= TAG_MAPPINGS_MAX_COUNT) {
      setIsLengthError(true)
      return
    }
    const exists = managementTags.find((tag) => tag.tagId === tagId)
    // NOTE: タグが紐付けられていなければ紐付けます
    if (exists) {
      setTagName('')
      return
    }
    await createTagMappingFunc(tagId)
    setTagName('')
    inputRef.current?.focus()
  }

  // onKeyPressRenameTagFunc タグ名を編集するか管理する関数です
  const onKeyPressRenameTagFunc = (
    event: KeyboardEvent<HTMLInputElement>,
    beforeTagName: string,
  ) => {
    if (event.key !== 'Enter') return

    // NOTE: タグ名が変更なかった場合はリクエストしない
    if (editTagName === beforeTagName) {
      setEditTagId(undefined)
      return
    }
    if (editTagName === '') {
      setIsTagValidationModalOpen(true)
      setTagValidationMessage('タグ名を入力してください')
      return
    }
    const tag = tags.find((tag) => tag.name === editTagName)
    if (tag) {
      setIsTagValidationModalOpen(true)
      setTagValidationMessage('このタグ名はすでに存在しています')
      return
    }
    setIsRenameTagModalOpen(true)
  }

  // onClickEditTag 3点リーダーのアイコンクリック時に state に編集するタグの情報を格納します
  const onClickEditTag = (tagId: number, tagName: string) => {
    setEditTagId(tagId)
    setEditTagName(tagName)
  }

  // displayTagName タグ名を表示します
  const TAG_LABEL_LIMIT = isSearch ? 9 : 15 // 3点リーダーで切り取る長さです
  const displayTagName = (tagName: string) => {
    return tagName.length > TAG_LABEL_LIMIT
      ? tagName.slice(0, TAG_LABEL_LIMIT) + '…'
      : tagName
  }

  return (
    <Wrapper>
      <OuterDiv onClick={() => close()} />
      <Position positions={positions}>
        <TagsManagementWrapper isSearch={isSearch}>
          <TagsManagementDiv>
            {managementTags.map((managementTag) => (
              <MappingTagLabel key={managementTag.tagId}>
                <MappingTagLabelName>
                  {displayTagName(managementTag.tagName)}
                </MappingTagLabelName>
                <a
                  onClick={async () =>
                    await deleteTagMappingFunc(
                      managementTag.tagId,
                      managementTag.tagMappingId || 0,
                    )
                  }
                >
                  <SmallCloseIcon />
                </a>
              </MappingTagLabel>
            ))}
            <InputTagDiv>
              <InputTag
                ref={inputRef}
                placeholder={
                  managementTags.length === 0 ? 'タグを検索する' : ''
                }
                value={tagName}
                onChange={(e) => setTagName(e.target.value)}
                onKeyPress={(event) => onKeyPressInputTag(event)}
                onKeyDown={(e) => onKeyDownCurrentIndex(e)}
              />
            </InputTagDiv>
          </TagsManagementDiv>
          <TagsManagementContent>
            <TitleDiv>
              <Title>{`タグを${
                isSearch ? '検索' : '選択'
              }するか作成します`}</Title>
              {displayTagMappingsCountError && (
                <Attention>{`※タグは最大${TAG_MAPPINGS_MAX_COUNT}つまで選択可能です。`}</Attention>
              )}
            </TitleDiv>
            {filteredTags.map((tag, index) => (
              <TagDiv
                key={tag.id}
                current={index === currentIndex}
                onMouseOver={() => setCurrentIndex(index)}
              >
                <ClickableTagDiv
                  onClick={() => onClickCreateTagMapping(tag.id)}
                >
                  <TagLabel>{displayTagName(tag.name)}</TagLabel>
                </ClickableTagDiv>
                <TagEditPopUpWrapper>
                  <GrayDotsIconDiv
                    onClick={() => onClickEditTag(tag.id, tag.name)}
                  >
                    <GrayDotsIcon />
                  </GrayDotsIconDiv>
                  {editTagId === tag.id && (
                    <>
                      <OuterDiv onClick={() => setEditTagId(undefined)} />
                      <TagEditPopUpDiv>
                        <TagEditPopUpMenu>
                          <PopUpMenuInput
                            value={editTagName}
                            onChange={(e) => setEditTagName(e.target.value)}
                            onKeyPress={(e) =>
                              onKeyPressRenameTagFunc(e, tag.name)
                            }
                            ref={editInputRef}
                          />
                          <TrashBoxWrapper>
                            <TrashBoxDiv
                              onClick={() => setIsDeleteTagModalOpen(true)}
                            >
                              <TrashBoxIcon />
                              <TrashBoxText>削除</TrashBoxText>
                            </TrashBoxDiv>
                          </TrashBoxWrapper>
                        </TagEditPopUpMenu>
                      </TagEditPopUpDiv>
                    </>
                  )}
                </TagEditPopUpWrapper>
              </TagDiv>
            ))}
            {canCreateTag && (
              <CreateTagDiv
                onClick={createTagAndMutate}
                current={currentIndex === CREATE_TAG_INDEX}
                onMouseOver={() => setCurrentIndex(CREATE_TAG_INDEX)}
              >
                <div>新規作成： </div>
                <TagLabel>{tagName}</TagLabel>
              </CreateTagDiv>
            )}
          </TagsManagementContent>
        </TagsManagementWrapper>
      </Position>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  position: relative;
  z-index: 2;
`
const Position = styled.div<{
  positions: TagsManagementCommonProps['positions']
}>`
  position: absolute;
  ${({ positions: { top, left, right, bottom } }) =>
    css`
      ${top !== undefined && `top: ${top}px;`}
      ${left !== undefined && `left: ${left}px;`}
      ${right !== undefined && `right: ${right}px;`}
      ${bottom !== undefined && `bottom: ${bottom}px;`}
    `}
  /* NOTE: 一番最後のルームのタグ管理を開いた時に下に余白をつけるため */
  padding-bottom: 100px;
`
const TagsManagementWrapper = styled.div<{ isSearch: boolean }>`
  width: ${({ isSearch }) => (isSearch ? 220 : 299)}px;
  background: ${truewhite};
  border: 1px solid ${_messagesecondary};
  box-shadow: 4px 8px 20px rgba(0, 0, 0, 0.18);
  border-radius: 6px;
  font-weight: ${fontWeightRegular};
  font-size: 14px;
`
const Spacer = css`
  margin-bottom: 6px;
`
const TagsManagementDiv = styled.div`
  padding: 7px 8px 1px 8px;
  display: flex;
  flex-wrap: wrap;
  line-height: 19px;
  background: ${_tags_management_background};
  border-radius: 5px 5px 0px 0px;
  border-bottom: 1px solid ${_messagesecondary};
`
const InputTagDiv = styled.div`
  ${Spacer};
  width: 100%;
`
const InputTag = styled.input`
  width: 100%;
`
const TagsManagementContent = styled.div`
  padding: 9px;
`
const TitleDiv = styled.div`
  font-size: 12px;
  margin-bottom: 8px;
`
const Title = styled.p`
  color: ${_message_submenu};
`
const Attention = styled.p`
  color: ${attentionColor};
`
const CurrentTagCss = css`
  border-radius: 4px;
  background: ${_tags_management_background};
  cursor: pointer;
`
const TagDiv = styled.div<{ current: boolean }>`
  display: flex;
  cursor: pointer;
  ${({ current }) => current && CurrentTagCss}
  justify-content: space-between;
  width: 100%;
  padding-right: 5px;
`
const ClickableTagDiv = styled.div`
  width: 100%;
  padding: 4px 5px 4px 8px;
`
const CreateTagDiv = styled.div<{ current: boolean }>`
  display: flex;
  cursor: pointer;
  ${({ current }) => current && CurrentTagCss}
  line-height: 20px;
  white-space: nowrap;
  margin-top: 4px;
  padding: 4px 8px;
`
export const TagLabelBase = css`
  padding: 0px 6px;
  line-height: 20px;
  background: ${_message_tag_label};
  border-radius: 3px;
  font-size: 14px;
`
const TagLabel = styled.span`
  ${TagLabelBase};
  overflow: hidden;
`
const MappingTagLabel = styled.div`
  ${Spacer}
  ${TagLabelBase};
  margin-right: 4px;
`
const MappingTagLabelName = styled.span`
  margin-right: 7px;
`
const TagEditPopUpWrapper = styled.div`
  position: relative;
  display: flex;
`
const GrayDotsIconDiv = styled.div`
  display: flex;
  align-items: center;
  margin: 4px 0;
  padding: 0px 3px;
  :hover {
    cursor: pointer;
    background: ${_message_tag_label};
    border-radius: 4px;
  }
`
const TagEditPopUpDiv = styled.div`
  position: absolute;
  z-index: 1;
  bottom: -90px;
  left: -180px;
`
const TagEditPopUpMenu = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  background: ${truewhite};
  border: 1px solid ${_messagesecondary};
  border-radius: 6px;
  cursor: auto; // FIXME: なぜかpointerになる
`
const PopUpMenuInput = styled.input`
  background: ${_tags_management_background};
  width: 198px;
  margin: 12px;
  padding: 0px 8px;
  line-height: 28px;
  border: 1px solid ${_messagesecondary};
  border-radius: 4px;
  :focus {
    border: 1px solid #02b7b0;
    box-shadow: 0px 0px 6px rgba(107, 194, 160, 0.7);
  }
`
const TrashBoxWrapper = styled.div`
  width: 100%;
  padding: 0px 6px 10px 6px;
`
const TrashBoxDiv = styled.div`
  display: flex;
  align-items: center;
  height: 28px;
  :hover {
    cursor: pointer;
    background: ${_message_tag_label};
    border-radius: 4px;
  }
`
const TrashBoxText = styled.a`
  font-weight: ${fontWeightBold};
  margin-left: 8px;
`
const OuterDiv = styled.div`
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  position: fixed;
  height: calc(100vh - ${HEADER_HEIGHT});
`
